import {devLog} from "@/lib/utils"
import useWebsocketStore from "@/stores/useWebsocketStore"
import {messageParse, messagesFromSocket} from "../lib/chat/messages"
import {useBotConfig} from "./useBotConfig"

const wsHost = process.env.REACT_APP_ADVISER_WS_HOST || document.location.host
const wsPrefixPath = process.env.REACT_APP_ADVISER_WS_PATH_PREFIX || ""
const SessionRequestParamSessionReconnect = "reconnect"
const responseTimout = 120000

export const useWebsocket = () => {
    const websocket = useWebsocketStore((state) => state.websocket)
    const setWebsocket = useWebsocketStore((state) => state.setWebsocket)
    const isConnected = useWebsocketStore((state) => state.isConnected)
    const setIsConnected = useWebsocketStore((state) => state.setIsConnected)
    const messages = useWebsocketStore((state) => state.messages)
    const addMessage = useWebsocketStore((state) => state.addMessage)
    const reconnectTimer = useWebsocketStore((state) => state.reconnectTimer)
    const setReconnectTimer = useWebsocketStore((state) => state.setReconnectTimer)
    const responseTimer = useWebsocketStore((state) => state.responseTimer)
    const setResponseTimer = useWebsocketStore((state) => state.setResponseTimer)
    const responseHasTimedOut = useWebsocketStore((state) => state.responseHasTimedOut)
    const setResponseHasTimedOut = useWebsocketStore((state) => state.setResponseHasTimedOut)

    const {cfg} = useBotConfig()

    const send = (msg: string) => {
        if (websocket?.readyState === WebSocket.CONNECTING) {
            // Wait for the connection to be ready.
            setTimeout(() => {
                send(msg)
            }, 100)
        }
        if (websocket?.readyState === WebSocket.OPEN) {
            websocket?.send(msg)

            // Handle if there is no response after a while
            setResponseTimer(setTimeout(setResponseHasTimedOut, responseTimout, true))
        }
    }

    // https://github.com/facebook/create-react-app/issues/10387
    const connect = (reconnect?: boolean) => {
        devLog("connecting to ws")
        if (websocket) {
            return
        }

        // Ensure the new connection is set to have not timed out.
        setResponseHasTimedOut(false)

        var protocol = "wss"
        if (window.location.protocol === "http:") {
            protocol = "ws"
        }
        let conf = Object.entries(cfg)
        if (reconnect) {
            conf.push(["session", SessionRequestParamSessionReconnect])
        }
        const filteredConf = conf.filter((e) => (e[1] ? e[1]?.length > 0 : false))
        const qs = filteredConf.map((e) => e.join("=")).join("&")
        const ws = new WebSocket(protocol + "://" + wsHost + wsPrefixPath + "/ws?" + qs)

        ws.onerror = (evt) => {
            ws?.close()
            //console.error(evt)
        }

        ws.onopen = () => {
            devLog("connected to ws")
            setIsConnected(true)

            reconnect = false
            if (reconnectTimer) {
                clearTimeout(reconnectTimer)
            }
        }

        ws.onclose = () => {
            devLog("disconnected from ws")
            setIsConnected(false)
            if (!reconnect) {
                // Try to connect again now.
                connect(true)
            } else {
                // Keep retrying
                setReconnectTimer(setTimeout(connect, 5000, true))
            }
        }

        ws.onmessage = (evt) => {
            if (responseTimer) {
                clearTimeout(responseTimer)
            }

            const mm = messagesFromSocket(evt)
            mm.forEach((m) => {
                const msg = messageParse(m)
                if (msg) {
                    //console.log(m)
                    addMessage(msg)
                }
            })
        }

        setWebsocket(ws)
        return ws
    }

    const closeWebsocket = (ws: WebSocket | undefined) => {
        if (ws?.readyState === WebSocket.OPEN) {
            ws.close()
        } else {
            ws?.addEventListener("open", () => {
                ws.close()
            })
        }
    }

    return {isConnected, messages, send, responseHasTimedOut, addMessage, connect, closeWebsocket, websocket}
}
